import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Clock, AlertTriangle, Crown, X } from 'lucide-react';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

export default function SubscriptionBanner() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && token) {
      fetchStatus();
    }
  }, [user, token]);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || dismissed || !status) return null;

  // Don't show banner for active subscribers with > 30 days remaining
  if (status.status === 'active' && status.days_remaining > 30) return null;

  // Trial users
  if (status.status === 'trial') {
    const urgency = status.days_remaining <= 2 ? 'urgent' : 'normal';
    
    return (
      <div className={`${urgency === 'urgent' ? 'bg-red-500/10 border-red-500/50' : 'bg-amber-500/10 border-amber-500/50'} border-b px-4 py-2`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className={`h-4 w-4 ${urgency === 'urgent' ? 'text-red-400' : 'text-amber-400'}`} />
            <span className={`text-sm ${urgency === 'urgent' ? 'text-red-400' : 'text-amber-400'}`}>
              {status.days_remaining === 0 ? (
                <strong>Your trial ends today!</strong>
              ) : status.days_remaining === 1 ? (
                <strong>1 day left</strong>
              ) : (
                <>{status.days_remaining} days left</>
              )} in your free trial
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className={`text-sm font-medium ${urgency === 'urgent' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'} text-zinc-900 px-4 py-1.5 rounded-lg transition-colors`}
            >
              Subscribe Now
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expiring subscription (< 30 days)
  if (status.status === 'active' && status.days_remaining <= 30) {
    return (
      <div className="bg-amber-500/10 border-b border-amber-500/50 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="h-4 w-4 text-amber-400" />
            <span className="text-sm text-amber-400">
              Your subscription expires in {status.days_remaining} days
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className="text-sm font-medium bg-amber-500 hover:bg-amber-600 text-zinc-900 px-4 py-1.5 rounded-lg transition-colors"
            >
              Renew Now
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expired subscription or trial
  if (status.status === 'expired') {
    return (
      <div className="bg-red-500/20 border-b border-red-500/50 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <span className="text-sm text-red-400 font-medium">
              Your access has expired. Subscribe to continue learning.
            </span>
          </div>
          <button
            onClick={() => navigate('/pricing')}
            className="text-sm font-medium bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg transition-colors"
          >
            Subscribe Now - R2,999/year
          </button>
        </div>
      </div>
    );
  }

  return null;
}
